import { observer } from "mobx-react-lite";
import { Flex } from "@chakra-ui/react";

import { mean } from "@noli/core";
import { useCornerRadius, useSelecting } from "@noli/business";

import "./BasicEditor.scss";
import { genBlock } from "@/utils/bem";
import { ReactComponent as RotateIcon } from "@/assets/icons/rotate.svg";
import { ReactComponent as CornerRadiusIcon } from "@/assets/icons/corner-radius.svg";
import Icon from "@/components/Icon";
import Property, { IProperty } from "@/components/Property";

function BasicProperty({ children, ...others }: IProperty<number>) {
  return (
    <Property w="90px" h="32px" {...others}>
      {children}
    </Property>
  );
}

const block = genBlock("c-basic-editor");

function BasicEditor() {
  const info = useSelecting("basic")({ fixed: 1 });
  if (!info) {
    return null;
  }
  const { type, x, y, w, h, rotation, displayNode, setX, setY, setW, setH, setRotation } = info;

  if (type === "none" || type === "multiple") {
    return null;
  }

  const alias = displayNode.alias;
  const { cornerRadius, setCornerRadius } = useCornerRadius();

  return (
    <Flex w="100%" p="16px" wrap="wrap" justify="space-between">
      <BasicProperty
        identifier={`${alias}-X`}
        text="X"
        value={x}
        setValue={setX({ trace: true })}
      />
      <BasicProperty
        identifier={`${alias}-Y`}
        text="Y"
        value={y}
        setValue={setY({ trace: true })}
      />
      <BasicProperty
        identifier={`${alias}-W`}
        text="W"
        value={w}
        setValue={setW({ trace: true })}
      />
      <BasicProperty
        identifier={`${alias}-H`}
        text="H"
        value={h}
        setValue={setH({ trace: true })}
      />
      <BasicProperty
        identifier={`${alias}-Rotation`}
        icon={<Icon svg={RotateIcon} className={block({ e: "icon" })} fillbyCurrentColor />}
        value={rotation}
        setValue={setRotation({ trace: true })}
      />
      {displayNode.type === "image" ? (
        <BasicProperty
          identifier={`${alias}-Corner-Radius`}
          icon={<Icon svg={CornerRadiusIcon} className={block({ e: "icon" })} fillbyCurrentColor />}
          value={cornerRadius ? parseFloat(mean(Object.values(cornerRadius)).toFixed(2)) : 0.0}
          setValue={(v) => setCornerRadius({ trace: true })({ lt: v, rt: v, rb: v, lb: v })}
        />
      ) : null}
    </Flex>
  );
}

export default observer(BasicEditor);
